import type { ManualGrantRequest, RecalculateRequest } from '$lib/api/types';

export type LeaveAdminActionKind = 'add' | 'deduct' | 'recalculate' | 'carryover';

export type LeaveAdminActionFormValues = {
	action: LeaveAdminActionKind;
	year: number | string;
	days: number | string | null | undefined;
	reason: string;
};

type LeaveAdminActionRequest = ManualGrantRequest | RecalculateRequest;

type ValidationResult =
	| { ok: true; request: LeaveAdminActionRequest }
	| { ok: false; message: string };

const MIN_YEAR = 2000;
const MAX_YEAR = 2100;
const MAX_DAYS = 365;
const MAX_REASON_LENGTH = 500;

export function validateLeaveAdminActionForm(values: LeaveAdminActionFormValues): ValidationResult {
	const year = parseInteger(values.year);
	if (year == null || year < MIN_YEAR || year > MAX_YEAR) {
		return { ok: false, message: '대상 연도는 2000년부터 2100년 사이로 선택해주세요.' };
	}

	const reason = values.reason.trim();
	if (reason.length > MAX_REASON_LENGTH) {
		return { ok: false, message: '사유는 500자 이하로 입력해주세요.' };
	}

	if (values.action === 'recalculate') {
		return {
			ok: true,
			request: compactRequest({ year, reason })
		};
	}

	const days = parseNumber(values.days);
	if (days == null) {
		return { ok: false, message: '일수를 입력해주세요.' };
	}
	if (days <= 0 || days > MAX_DAYS) {
		return { ok: false, message: '일수는 0일 초과 365일 이하로 입력해주세요.' };
	}

	return {
		ok: true,
		request: compactRequest({ year, days, reason })
	};
}

function parseInteger(value: number | string): number | null {
	const parsed = typeof value === 'number' ? value : Number(String(value).trim());
	return Number.isInteger(parsed) ? parsed : null;
}

function parseNumber(value: number | string | null | undefined): number | null {
	const raw = typeof value === 'number' ? String(value) : (value ?? '').trim();
	if (!raw) return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
}

function compactRequest<T extends Record<string, string | number | undefined>>(request: T): T {
	return Object.fromEntries(
		Object.entries(request).filter(([, value]) => value !== undefined && value !== '')
	) as T;
}
